import { AccountFlag } from "../api/fetchAccountMappings/types";

/**
 * Which CertainPath accounts the list shows.
 *
 * The three states are disjoint and assigned by precedence — a row holding one
 * unconfirmed mapping alongside two confirmed ones is `review`, because that is
 * the work still outstanding. They therefore sum to the catalog, which the
 * previous split did not: "Needs attention" mixed empty rows with unreviewed
 * ones, and "Has accounts" overlapped both, so the counts never reconciled.
 */
export type LedgerViewTab = "all" | "unmapped" | "review" | "confirmed";

/**
 * Ordered by the life of a mapping — everything, then nothing mapped, then
 * mapped but unconfirmed, then done — rather than by urgency. The view opens on
 * whichever tab holds the work, so the leftmost slot does not have to.
 */
export const LEDGER_VIEW_TABS: { id: LedgerViewTab; label: string }[] = [
  { id: "all", label: "All accounts" },
  { id: "unmapped", label: "Not mapped" },
  { id: "review", label: "Requires review" },
  { id: "confirmed", label: "Confirmed" },
];

/**
 * The count badge on each tab, coloured by what the tab holds so the row reads
 * as a state of the work rather than four identical grey pills.
 *
 * `all` stays neutral — it is a view, not a state, and tinting it would compete
 * with the three that mean something. Only the inactive tab uses these; the
 * selected tab's badge goes white on the crimson fill, which the
 * `group-data-[state=on]` variants override with higher specificity.
 */
export const LEDGER_TAB_BADGE_CLASSES: Record<LedgerViewTab, string> = {
  all: "bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-300",
  // Solid fills, and borrowed rather than invented: the same crimson the
  // selected tab fills with, and the same blue the unmapped notice draws along
  // its bottom edge, so a colour means one thing across the section.
  unmapped: "bg-primary text-white",
  // Gold takes dark text — white on a fill this bright fails contrast outright.
  review: "bg-amber-400 text-amber-950",
  // Blue rather than the emerald "confirmed" carries elsewhere: it reads as
  // settled without turning the row into a wall of green next to the success
  // band, and keeps the three meaningful tabs in three distinct hues.
  confirmed: "bg-blue-500 text-white",
};

/**
 * Where a row sits, most-urgent-wins. `all` is a view rather than a state, so
 * it is never returned here.
 */
export const ledgerRowTab = (
  mappedCount: number,
  pendingCount: number,
): Exclude<LedgerViewTab, "all"> => {
  if (mappedCount === 0) {
    return "unmapped";
  }

  return pendingCount > 0 ? "review" : "confirmed";
};

/**
 * The "a machine did this" treatment, shared by the AI-mapped chip and the
 * Auto-map button that produces those mappings.
 *
 * A gradient ring around a flat fill, which CSS cannot express with
 * border-color. Two stacked backgrounds do it: the fill clipped to padding-box,
 * the gradient clipped to border-box, showing through only where the
 * transparent border sits. Both must live in one `background` shorthand, or the
 * second declaration wipes the first — which is also why the dark variant
 * restates the ring rather than overriding the fill alone.
 */
export const AI_GRADIENT_RING_CLASSES = [
  "border-2 border-transparent",
  "[background:linear-gradient(#ffffff,#ffffff)_padding-box,linear-gradient(to_right,#ec4899,#8b5cf6,#22d3ee)_border-box]",
  "text-indigo-800",
  "dark:[background:linear-gradient(#241f47,#241f47)_padding-box,linear-gradient(to_right,#ec4899,#8b5cf6,#22d3ee)_border-box]",
  "dark:text-white",
].join(" ");

/**
 * A CertainPath row's state, in the order the member should care about it.
 *
 * `gap` is the only one that means "you must act": a KPI names this account and
 * nothing is in it. `pending` means a suggestion is waiting to be accepted.
 *
 * `empty` used to be silent, on the grounds that plenty of accounts are
 * legitimately empty for any given member. That is true of an account nothing
 * reads and false of one a KPI sums — leaving Other Operating Expenses empty
 * blanks no card, it makes Net Profit % read high — and rendering the two
 * identically is what taught members that an unbadged row was optional.
 *
 * So `empty` stays one state and carries a note when the row is summed. Not a
 * fifth state: the row's own status is unchanged, and what differs is a fact
 * about the catalog rather than about the member's progress.
 */
export type LedgerRowState = "gap" | "pending" | "filled" | "empty";

/**
 * Every row carries the left border so text does not shift by 4px when a row
 * changes state. Side-specific `border-l-*` is deliberate — a bare
 * `border-rose-400` would repaint the row's bottom border too.
 */
export const LEDGER_ROW_CLASSES: Record<LedgerRowState, string> = {
  // A gap is the only state worth tinting — it is the one the member has to
  // act on, and the tint is what lets them find it by scrolling rather than by
  // reading every row. Everything else carries the accent bar alone, so the
  // list stays white and the chips inside it keep their contrast.
  gap: "border-l-4 border-l-rose-400 bg-rose-50/50 dark:bg-rose-950/20",
  pending: "border-l-4 border-l-amber-400",
  filled: "border-l-4 border-l-transparent",
  empty: "border-l-4 border-l-transparent",
};

/**
 * What a section heading says when every account in one of its required groups
 * is empty.
 *
 * Keyed by the group key the API returns. These sit on the heading rather than
 * on a row because any one member satisfies the requirement — telling a member
 * who keeps only owner capital that Retained Earnings is missing would be a gap
 * they can never close.
 *
 * Only two groups are left. Revenue, liabilities and kept profit are each a
 * single CertainPath account now, so the API marks their rows required outright
 * and never returns them as a group key.
 */
export const REQUIRED_GROUP_COPY: Record<string, string> = {
  opex: "Nothing is mapped to operating expenses yet. Days Cash on Hand measures how long your cash lasts against what you spend, so it needs at least one.",
  equity:
    "Nothing is mapped to equity yet. Debt to Equity compares what you owe against what you own, so it needs at least one.",
};

export const DO_NOT_MAP_CODE = "do_not_map";

/**
 * What belongs in a CertainPath account, in the member's own vocabulary.
 *
 * A row earns one when its section asks the member to *sort* between rows, not
 * when it asks them to *find* one. Direct Costs and Operating Expenses are
 * sorting problems: several plausible homes, of which only some are named by a
 * KPI, and one is the residual that cannot say so in its own label. Assets,
 * Liabilities and Equity are find-the-name — QuickBooks uses the same words the
 * labels do, and a description there would only restate the label. Without that
 * line every row ends up carrying grey text and none of it gets read.
 *
 * Do Not Map is the exception to the rule and earns one on consequence instead:
 * it drops an account from every KPI and from both sides of reconciliation.
 * `ACCOUNT_FLAG_COPY.excluded` says the same thing, but only once the mapping
 * exists — this says it while the member is still deciding.
 */
export const LEDGER_ACCOUNT_DESCRIPTIONS: Record<string, string> = {
  field_labor:
    "Wages and burden for technicians and installers — everyone whose time goes to a job.",
  materials_equipment:
    "Parts, equipment and materials that go into the jobs you run.",
  other_direct_cost:
    "Permits, subcontractors, equipment rental, dump fees — job costs that aren't wages or parts.",
  office_labor:
    "Wages and burden for owners, officers, CSR, dispatch and management staff — everyone not on a job.",
  marketing:
    "Advertising, lead generation and anything else spent to bring work in.",
  overhead:
    "Rent, utilities, vehicles, insurance, office and general costs — everything else it takes to run the business.",
  [DO_NOT_MAP_CODE]:
    "Accounts that should reach no KPI at all. Excluded from every card, and from both sides of reconciliation.",
};

interface AccountFlagCopy {
  label: string;
  text: string;
  isWarning: boolean;
}

/**
 * Warnings mean the mapping alone cannot produce a correct KPI and someone has
 * to decide something. Notes just explain what happened.
 */
export const ACCOUNT_FLAG_COPY: Record<AccountFlag, AccountFlagCopy> = {
  payroll_split: {
    label: "May mix field & office wages",
    text: "Payroll posted to an expense account can mix field and office wages, which no account mapping can split. Allocate by employee, or split the account in QuickBooks — Field Labor % and Office Labor % both depend on this.",
    isWarning: true,
  },
  // Sits alongside payroll_split rather than replacing it: the split is a fact
  // about the member's books, this is a mistake in the mapping. Only the second
  // one can be fixed on this page.
  payroll_outside_labor: {
    label: "Wages not counted as labor",
    text: "This is payroll, but it is mapped outside Field Labor and Office Labor — so none of it reaches Field Labor % or Office Labor %, and both cards read lower than they should. Move it to whichever labor account it belongs to.",
    isWarning: true,
  },
  materials_in_overhead: {
    label: "Materials in overhead",
    text: "Looks like job materials posted to overhead. Left here they understate direct costs and overstate gross margin — consider reclassing to a Cost of Goods Sold account in QuickBooks.",
    isWarning: true,
  },
  payroll_burden: {
    label: "Burden follows wages",
    text: "Payroll burden maps with the wages it belongs to — field burden to Field Labor, office burden to Office Labor.",
    isWarning: false,
  },
  excluded: {
    label: "Excluded from KPIs",
    text: "Excluded, so it never reaches a KPI. It also drops out of both sides of reconciliation.",
    isWarning: false,
  },
};

/**
 * Shown when the chosen CertainPath account sits in a different section than
 * QuickBooks' own classification. Deliberately not a flag — it follows from the
 * mapping the member picked, not from anything about the account itself.
 */
export const CROSS_TYPE_NOTICE = {
  label: "Reclassified",
  text: "This maps to a CertainPath account in a different section than QuickBooks classifies it under — intentional for cases like field wages posted to an Expense account, but worth a second look.",
};

/**
 * Whether a QuickBooks account looks like it belongs in a CertainPath account,
 * judged on the subtype QuickBooks itself assigns.
 *
 * The picker's "Likely matches" tier and the row's suggestion for a retired
 * account holding a balance are the same question asked from opposite ends —
 * one from the CertainPath account outwards, one from the QuickBooks account in
 * — so they read the same answer from here rather than each carrying a copy.
 */
export const isLikelySubtypeMatch = (
  accountSubType: string | null,
  qboSubtypeMatches: string[],
): boolean =>
  accountSubType !== null &&
  qboSubtypeMatches.some(
    (subType) => subType.toLowerCase() === accountSubType.toLowerCase(),
  );
